// The path every Amazon building follows to COD, from the SPPA, the MSPPA and Amazon's
// EUSDR (with Exhibit 9). Content, not data: the
// statuses come from POST /amazon-overview/buildings. Step ids must agree with STEP_IDS in
// src/inrange_agents/amazon_overview.py. docs/Y2026-Q4/design-amazon-overview.md

export type LaneId = "legal" | "grid" | "design" | "site";
export type Owner = "inrange" | "amazon" | "third" | "both";

export interface Lane {
  id: LaneId;
  name: string;
  sub: string;
}

export interface PathStep {
  id: string;
  lane: LaneId;
  /** The story chapter it belongs to, 0 first (chapters.ts). */
  chapter: number;
  owner: Owner;
  ownerLabel?: string;
  title: string;
  desc: string;
  /** The process for this step is not agreed yet. */
  unapproved?: boolean;
  /** Design approval: opens Amazon's three parallel reviews. */
  reviews?: boolean;
  /** Shown as a part of this step's box rather than a box of its own. */
  inBox?: string;
  /** The parts a box shows, each a step with its label, in the order they happen. */
  halves?: [string, string][];
  /** One for the whole portfolio, not one per building. */
  portfolio?: boolean;
  /** Not drawn on the Step 1 story, though Steps 2 and 3 still show it. */
  offMap?: boolean;
  /** Our own step, not part of the story told to Amazon: Steps 1 and 3 leave it out. */
  internal?: boolean;
  /** Step 1 draws the box level with the top or the middle of another box in its chapter
   * (Noaa, 20 Oct 2026). */
  mapLevel?: { with: string; at: "top" | "middle" };
  /** Must be done before this step can start. */
  deps: string[];
  /** Must be done before this step can finish. */
  endDeps?: string[];
  /** Planned length in days; null where something else sets it. */
  days: number | null;
  /** ``days`` is our own estimate, not a period the contract gives. */
  ownEstimate?: true;
  subs: string[];
  /** What this step needs from Amazon, with the clause behind it. */
  needs: StepNeed[];
  source: string;
}

/** One thing a step needs from Amazon (src/inrange_agents/amazon_needs.py keeps the same
 * list, in the same order). */
export interface StepNeed {
  text: string;
  source: string;
  /** Only some buildings need it: those with existing PV, or roofs with skylights. */
  only?: "existingPv" | "skylights";
  /** Where it sits in its step's list (Noaa, 19 Oct 2026); none on a step whose list has
   * none. */
  category?: string;
}

/** The maximum time the contract gives a step, with the clause behind it. */
export interface StepLimit {
  label: string;
  /** Amazon can hold us here with no limit, or a long one. */
  risk: boolean;
  /** There is no limit at all: the map's box carries no tag, and its pane says so. */
  unbounded?: true;
  clause: string;
  reference: string;
  /** The contract's own words. */
  text: string;
}

export const LANES: Lane[] = [
  {
    id: "legal",
    name: "Legal",
    sub: "Contracts and consents",
  },
  {
    id: "grid",
    name: "Grid",
    sub: "DNO connection",
  },
  {
    id: "design",
    name: "Survey & design",
    sub: "Through Amazon approval",
  },
  {
    id: "site",
    name: "Site & Build",
    sub: "Site access to COD",
  },
];

export const STEPS: PathStep[] = [
  {
    id: "prelim",
    lane: "design",
    chapter: 0,
    owner: "inrange",
    title: "Preliminary max-out design",
    desc: "Feeds grid, surveys",
    deps: [],
    days: 14,
    subs: [
      "Largest viable system for the site: module layout and SLD",
      "Goes to the DNO as the grid application, and sets what the site and roof surveys check",
    ],
    needs: [
      {
        text: "Half-hourly consumption data for sizing, and the annual consumption profile for the yield study",
        source: "MSPPA s6.3(b)(i)",
      },
      {
        text: "Prompt notice of any expected change in the building's demand: new EV fleets, new connections or MPANs, mezzanines, a change of use",
        source: "MSPPA s6.2(b)",
      },
    ],
    source:
      "EUSDR Exhibit 9 s1.1.9, s1.1.10; MSPPA s3.4; MSPPA definition of Preliminary Design Submission, item (ii)",
  },
  {
    id: "access0",
    lane: "site",
    chapter: 0,
    owner: "amazon",
    title: "Initial site access",
    desc: "For first surveys",
    mapLevel: { with: "survey", at: "top" },
    deps: [],
    days: 0,
    subs: [
      "First visits for the PV, roof and installation surveys",
      "Avetta / Right Crowd clearance, escort where needed",
    ],
    needs: [],
    source:
      "Best Practices s1, s2.4; SPPA Sch 11; MSPPA s8.2(c)(iv), s20.16(a)",
  },
  {
    id: "gridapp",
    lane: "grid",
    chapter: 0,
    owner: "inrange",
    title: "Grid application",
    desc: "Well before signing",
    deps: ["prelim"],
    days: 0,
    subs: [
      "Max-size preliminary layout and SLD sent to the DNO, fees paid",
      "Schedule 1 records the DNO submission date",
    ],
    needs: [
      {
        text: "All reasonable help with the DNO application, including any letter of authority we need to file it",
        source: "MSPPA s2.6(c)(i)",
      },
      {
        text: "The HV SLD and supply agreement, supply capacity, meters, MPANs and metering preferences, how the feeders run, the point of connection and DNO interface limits",
        source: "SPPA Sch 11 cl 4.8(ii)",
      },
      {
        text: "The connection agreement and as-installed SLD for the existing PV, or its original application reference",
        source: "SPPA Sch 1",
        only: "existingPv",
      },
    ],
    source:
      "SPPA Sch 1 DNO Submission Date; MSPPA s3.5, s2.6(c)(i); EUSDR definition of DNO/DSO Application",
  },
  {
    id: "survey",
    lane: "design",
    chapter: 0,
    owner: "inrange",
    title: "Initial site & roof survey",
    desc: "In parallel with grid",
    deps: ["access0", "prelim"],
    days: 21,
    subs: [
      "On-site PV and installation survey",
      "Roof condition pre-assessment",
      "Shading, drainage and pathway survey",
    ],
    needs: [],
    source: "EUSDR Exhibit 9 s1.2; EUSDR 3.1.5; MSPPA s4.1(a)(xii)",
  },
  {
    id: "structural",
    lane: "design",
    chapter: 1,
    owner: "inrange",
    title: "Structural assessment",
    desc: "Ours · sets achievable kWp",
    mapLevel: { with: "llpack", at: "middle" },
    deps: ["survey"],
    days: 28,
    subs: [
      "Load assessment to Eurocode and Amazon key design indicators",
      "Signed by a certified engineer",
      "Its outcome sets the achievable kWp",
    ],
    needs: [
      {
        text: "Any structural surveys of the building Amazon already holds",
        source: "MSPPA s2.5(a)",
      },
      {
        text: "Confirmation of the tenant loads already on the structure, such as conveyors, fans and sprinklers",
        source: "EUSDR 3.1.6(1)",
      },
      {
        text: "Amazon structural Key Design Information (KDIs)",
        source: "EUSDR 3.1.6, Exhibit 8",
      },
      {
        text: "Amazon's written agreement if we do not use the landlord's structural engineer, as we otherwise should",
        source: "EUSDR Exhibit 9 s1.2(7)",
      },
      {
        text: "Prompt notice of any change of use or building works that changes the interior loads, and so the structural assessment",
        source: "",
      },
    ],
    source: "EUSDR 3.1.6(1), Exhibit 8, Exhibit 9 s1.2(7)",
  },
  {
    id: "sppa",
    lane: "legal",
    chapter: 1,
    owner: "both",
    title: "SPPA signed",
    desc: "Starts the clocks",
    deps: [],
    days: 0,
    subs: [
      "Preliminary design (DR2) and grid application done before signing",
      "Site Licence runs from the Effective Date",
      "Starts the 10 Business Day site-information and 30-day guarantee clocks",
    ],
    needs: [
      {
        text: "The site licence in the Schedule 11 form, entered into by Amazon from the SPPA's Effective Date",
        source: "MSPPA s8.2(c)(i)",
      },
    ],
    source: "SPPA preamble, Sch 11; MSPPA s3.4",
  },
  {
    id: "gridoffer",
    lane: "grid",
    chapter: 1,
    owner: "third",
    ownerLabel: "DNO",
    title: "Grid offer",
    desc: "Offers come in gradually based on DNO availability",
    mapLevel: { with: "llpack", at: "middle" },
    deps: ["gridapp"],
    days: null,
    subs: [
      "The DNO issues the connection offer, which sets the achievable kWac for the grid",
      "Where still pending, planned on the date the DNO told us to expect it",
      "DNO approvals go into the approvals list Amazon needs before approving the design",
    ],
    needs: [
      {
        text: "Amazon's written decision to bear or share any grid connection costs above the SPPA's Excess Interconnection Costs threshold",
        source: "MSPPA s2.6(c)(ii)",
      },
    ],
    source: "MSPPA s3.3(b)",
  },
  {
    id: "signoff",
    lane: "design",
    chapter: 2,
    owner: "amazon",
    title: "Structural sign-off",
    desc: "Amazon · before full design",
    deps: ["structural"],
    days: 0,
    subs: [
      "Amazon signs off the structural assessment before full design starts",
      "The contract sets no time limit for it",
      "Waiting here holds up detailed design",
    ],
    needs: [
      {
        text: "The as-built documentation, which the survey checks for gaps that affect the layout",
        source: "MSPPA s2.5(a); EUSDR Exhibit 9 s1.2",
        category: "Documentation",
      },
      {
        text: "Amazon's natural hazard risk assessment for the site before design starts, or confirmation that the site has no significant exposure",
        source: "EUSDR Exhibit 4",
        category: "Documentation",
      },
    ],
    source: "EUSDR 3.1.6(1)",
  },
  {
    id: "llpack",
    lane: "legal",
    chapter: 1,
    owner: "inrange",
    halves: [
      ["llpack", "Pack to Amazon"],
      ["llsent", "Sent to landlord"],
      ["llfeedback", "Landlord feedback"],
    ],
    title: "Preliminary consent pack",
    desc: "With the preliminary designs",
    deps: ["prelim"],
    days: 0,
    subs: [
      "We send the consent pack, with the preliminary designs, to Amazon's asset manager",
      "The asset manager sends it on to the landlord",
      "The preliminary design package carries what Amazon needs to help the landlord review the system",
    ],
    needs: [
      {
        text: "Each landlord's own document list and conditions for consent, before the pack goes",
        source: "MSPPA s3.7(b)",
      },
      {
        text: "The legal landlord that signs the consent and the Schedule 17 deed, and its managing agent",
        source: "SPPA Sch 1, Sch 17",
      },
    ],
    source: "MSPPA definition of Preliminary Design Submission, item (H)",
  },
  {
    id: "llsent",
    lane: "legal",
    chapter: 1,
    owner: "amazon",
    title: "Sent to landlord",
    desc: "Amazon's asset manager sends our pack on",
    inBox: "llpack",
    deps: ["llpack"],
    days: 0,
    subs: [
      "Amazon's asset manager sends our preliminary pack on to the landlord",
      "Done when the asset manager or the landlord tells us it arrived",
    ],
    needs: [
      {
        text: "Send our preliminary consent pack on to the landlord, and tell us when it has gone",
        source: "",
      },
    ],
    source: "Not set by the contract",
  },
  {
    id: "llfeedback",
    lane: "legal",
    chapter: 1,
    owner: "third",
    ownerLabel: "Landlord",
    title: "Landlord feedback",
    desc: "The landlord comes back on the preliminary pack",
    inBox: "llpack",
    deps: ["llsent"],
    days: null,
    subs: [
      "The landlord comes back on the preliminary pack",
      "Its comments go into the detailed design",
    ],
    needs: [],
    source: "Not set by the contract",
  },
  {
    id: "lldpack",
    lane: "legal",
    chapter: 3,
    inBox: "dr3",
    owner: "inrange",
    title: "Detailed consent pack",
    desc: "With the detailed design",
    deps: ["design", "llfeedback"],
    days: 0,
    subs: [
      "The detailed design submission, which also gives the landlord its pack",
      "Plus any other information the landlord or its engineer of record reasonably asks for",
    ],
    needs: [
      {
        text: "The landlord's collateral warranty form: who it names, which contractors, and when",
        source: "",
      },
    ],
    source:
      "MSPPA s3.6(f), s8.2; MSPPA definition of Detailed Design Submission",
  },
  {
    id: "llsignoff",
    lane: "legal",
    chapter: 3,
    inBox: "dr3",
    owner: "third",
    ownerLabel: "Landlord",
    title: "Landlord sign-off",
    desc: "Landlord Approval of the detailed design",
    deps: ["lldpack"],
    // Noaa, 2026-10-18: the contract gives the landlord's review no length.
    days: 30,
    ownEstimate: true,
    subs: [
      "Landlord Approval of the detailed design, by the Landlord Approval Date in Schedule 1",
      "Amazon obtains it; we cooperate in good faith",
      "Either party can extend the date by up to 90 days",
      "Our access to the site to install is subject to it",
    ],
    needs: [
      {
        text: "The landlord's technical approvals in the Licence to Alter: roof changes for the racking, membrane fixing, drilling into roof panels, and any switchgear change",
        source: "MSPPA s3.7(b); EUSDR 3.1.8; Best Practices s2.3",
      },
      {
        text: "Any superior landlord, funder, insurer or roof warrantor consent the lease needs",
        source: "MSPPA s1.1 Landlord Approval",
      },
      {
        text: "The landlord's signed Landlord Acknowledgement Deed",
        source: "SPPA Sch 11 cl 6.1, Sch 17",
      },
    ],
    source:
      'MSPPA s3.7(b), (c), s3.8(a), s8.2, definition of "Landlord Approval Date"; SPPA Sch 1',
  },
  {
    id: "access2",
    lane: "site",
    chapter: 2,
    owner: "amazon",
    title: "Detailed site access",
    desc: "Roof inspection, switchgear",
    deps: ["sppa"],
    days: 0,
    subs: [
      "Access for the independent roof inspection, detailed surveys and switchgear discovery",
      "Visitor list and dates 2 weeks ahead; every visitor passes Amazon's compliance audit",
      "Switchgear discovery needs an outage window Amazon coordinates",
    ],
    needs: [
      {
        text: "A switchgear discovery outage outside the Restricted Period (15 Oct to 15 Jan), Site Leadership's sign-off of the method, and consent for interior access in it",
        source: "Best Practices s2.3; MSPPA s4.2(c), s5.8(c), (d)",
        category: "Site outage",
      },
      {
        text: "Notice, as early as practicable, of planned works, repairs or alterations by Amazon or the landlord that may affect the system or our access",
        source: "SPPA Sch 11 cl 4.4",
        category: "Other",
      },
    ],
    source:
      "SPPA Sch 11; MSPPA s4.2(c), s8.2(c); Best Practices s2.3, s2.4; EUSDR 3.1.4, Exhibit 9 s3.11",
  },
  {
    id: "gridacc",
    lane: "grid",
    chapter: 2,
    owner: "inrange",
    internal: true,
    title: "Offer accepted",
    desc: "Before the accept-by date",
    deps: ["gridoffer"],
    days: 30,
    subs: [
      "We accept the offer and secure the connection works",
      "Planned on the offer's accept-by date",
    ],
    needs: [],
    source: "DNO offer terms",
  },
  {
    id: "inputs",
    lane: "legal",
    chapter: 2,
    offMap: true,
    owner: "amazon",
    title: "Amazon design inputs",
    desc: "Click for the list",
    deps: ["sppa"],
    days: 0,
    subs: [
      "Disclosed Site Information index and PDF pack, 10 Business Days after signing",
      "Help with the DNO application, including any letter of authority",
      "Where the MFL walls are",
      "Existing lightning protection details and earlier studies",
      "Point of connection, metering preferences and DNO interface limits",
      "Approval of our preferred equipment list and any deviations",
      "Confirmation of the tenant loads already on the structure",
      "The building's existing fire strategy",
      "Where our monitoring connects to the building network",
      "Any site data and studies Amazon holds: climate, noise, ground, hydrology",
      "Agreement to our fixing method, and the landlord's approval of roof changes",
    ],
    needs: [],
    source:
      "EUSDR 3.1.4, 3.1.5, 3.3.1, 3.4.1, Exhibit 9 s1.2; SPPA Sch 1; MSPPA s2.5, s2.6(c)(i), s3.2(b)",
  },
  {
    id: "design",
    lane: "design",
    chapter: 2,
    owner: "inrange",
    title: "Detailed design",
    desc: "Needs sign-off and inputs",
    deps: ["signoff", "inputs", "access2"],
    days: 42,
    subs: [
      "Full design package (EUSDR Exhibit 1.1) checked against the 195-point spec list (Exhibit 1.2)",
      "Layout clear of exclusion zones and MFL walls",
      "Fixing method agreed with Amazon; roof manufacturer warranty letters",
      "Fire risk assessment, earthing and lightning studies",
      "Submitting it also gives the landlord its pack",
    ],
    needs: [
      {
        text: "Any data and studies Amazon holds on the site's climate, background noise, ground and hydrology",
        source: "MSPPA s3.2(b)",
        category: "General site info",
      },
      {
        text: "Any restrictions on Amazon's use of the site that the installation must follow, notified before the SPPA is signed",
        source: "MSPPA s4.1(a)(xii)",
        category: "General site info",
      },
      {
        text: "Where the MFL walls are (no equipment within 5 m), and who on site confirms the building's exclusion zones",
        source: "EUSDR 3.1.5",
        category: "PV layout",
      },
      {
        text: "The exclusion zones: offices and future build-out, electrical rooms, climate-controlled areas, tenant carve-outs, plant, and Amazon and landlord project zones",
        source: "EUSDR 3.1.5",
        category: "PV layout",
      },
      {
        text: "Edge and smoke hatch distances, smoking and rest areas, and any rooftop hydrogen tubing or blow-off valves",
        source: "EUSDR 3.1.5, 3.1.8, 3.4.2; Best Practices s6",
        category: "PV layout",
      },
      {
        text: "Amazon's agreement to our fixing method before works start, and the landlord's approval of any roof modification",
        source: "EUSDR 3.1.8(2), (3), (6)",
        category: "PV layout",
      },
      {
        text: "The fire strategy and fire risk assessment and who produced them, which our PV fire risk assessment checks against, and help with the fire authority",
        source: "EUSDR 3.4.1; MSPPA s3.3(a)",
        category: "Fire",
      },
      {
        text: "The Amazon Fire Strategy Team contact who agrees any deviation from the EUSDR's riser requirement",
        source: "",
        category: "Fire",
      },
      {
        text: "Existing lightning protection drawings, test certificates and any earlier studies",
        source: "EUSDR 3.2.11",
        category: "Electrical",
      },
      {
        text: "Whether standby or temporary generators feed the LV system, where they connect, and the changeover procedure",
        source: "EUSDR 3.2.3.9",
        category: "Electrical",
      },
      {
        text: "One named Amazon decision-maker who approves inverter and PVDB locations, cable routes, the compound and storage, the crane and lifting plan, and spare LV ways",
        source: "MSPPA s8.2(d); Best Practices Introduction",
        category: "Electrical",
      },
      {
        text: "Point of connection and any DNO interface limits",
        source: "",
        category: "Electrical",
      },
      {
        text: "The existing PV's connection agreement and as-installed SLD, which the detailed design has to work around",
        source: "SPPA Sch 1",
        only: "existingPv",
        category: "Existing PV",
      },
      {
        text: "Who owns and maintains the existing PV, its O&M contract, status and real size, and the isolation procedure for working beside it",
        source: "SPPA Sch 1, Sch 2 s6.6(a)",
        only: "existingPv",
        category: "Existing PV",
      },
      {
        text: "Written approval that Amazon pays for our assessment of the existing PV and any fixes it finds",
        source: "SPPA Sch 2 s6.6",
        only: "existingPv",
        category: "Existing PV",
      },
      {
        text: "The documents the EUSDR relies on that we do not have: General Requirements, GAPS, the QA/QC appendices and the rest",
        source: "",
        category: "Documentation",
      },
      {
        text: "Where Amazon will connect our monitoring system to its building network",
        source: "EUSDR 3.3.1",
        category: "Other",
      },
      {
        text: "Approval of our preferred equipment list and any deviations",
        source: "",
        category: "Other",
      },
    ],
    source:
      "SPPA Sch 1 Detailed Design Submission Due Date; MSPPA s3.6; EUSDR Exhibit 9 s1.2, s1.5",
  },
  {
    id: "pcg",
    lane: "legal",
    chapter: 1,
    owner: "amazon",
    title: "Parent company guarantee",
    desc: "One for the whole portfolio",
    portfolio: true,
    deps: ["sppa"],
    days: 30,
    subs: [
      "Amazon.com, Inc. guarantee in the Schedule 16 form, one for the whole portfolio",
      "Until delivered we have no obligation to start construction, and every milestone moves day for day",
    ],
    needs: [],
    source: "SPPA Sch 2 s8.5",
  },
  {
    id: "roof",
    lane: "site",
    chapter: 2,
    owner: "both",
    title: "Roof inspection & remediation",
    desc: "Amazon's roof records first · can take months",
    unapproved: true,
    deps: [],
    days: 90,
    subs: [
      "Amazon gives us the roof records below before the inspection: without them the remediation cannot be scoped",
      "Independent roof inspection with water-leak test, photos, drainage and lightning protection",
      "Amazon approves the repairs the inspection finds",
      "Repairs done and roof certifications intact before mobilisation",
      "Who carries out and pays for remediation is still to be agreed with Amazon and the landlord",
    ],
    needs: [
      {
        text: "The labour warranty documents from the roof contractor of record",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Warranty",
      },
      {
        text: "The material warranty documents from the manufacturer",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Warranty",
      },
      {
        text: "The roof manufacturer's warranty policy and maintenance process",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Warranty",
      },
      {
        text: "The roof manufacturer's written approval of the PV attachment, and confirmation the roof warranty survives it",
        source: "EUSDR 3.1.8(8)",
        category: "Warranty",
      },
      {
        text: "How Amazon wants us to proceed where a roof warranty does not exist or cannot be found",
        source: "",
        category: "Warranty",
      },
      {
        text: "The roof manufacturer",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Records and inspection",
      },
      {
        text: "The type of roof",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Records and inspection",
      },
      {
        text: "The roof contractor of record",
        source: "MSPPA s5.5(b); EUSDR 3.1.4",
        category: "Records and inspection",
      },
      {
        text: "The roof manufacturer engaged for fixing-method letters",
        source: "",
        category: "Records and inspection",
      },
      {
        text: "Amazon's latest roof condition report for the building",
        source: "",
        category: "Records and inspection",
      },
      {
        text: "Roof maintenance, inspection and under-array cleaning records, which the roof guarantees require",
        source: "MSPPA s5.5(b)",
        category: "Records and inspection",
      },
      {
        text: "Roof access for the independent roof inspection, with its water leakage test, before installation",
        source: "EUSDR 3.1.4",
        category: "Records and inspection",
      },
      {
        text: "The roof build-up and its reaction-to-fire class, whether Amazon treats the site as a legacy site, and whether the AMZL non-combustible rule applies",
        source: "EUSDR 3.2.2.7, 3.4.2",
        category: "Fire",
      },
      {
        text: "Who provides fixed fall protection for the roof's skylights",
        source: "",
        only: "skylights",
        category: "Safety",
      },
      {
        text: "Agreement on who fixes what the roof inspection finds, as repairs fall to us if we mobilise despite an insufficient report",
        source: "EUSDR 3.1.4",
        category: "Remediation",
      },
      {
        text: "Whether each repair the inspection finds is planned (PPM) or reactive maintenance, which decides whether it can go ahead in Amazon's freeze",
        source: "",
        category: "Remediation",
      },
      {
        text: "Who approves the maintenance request (MCM) and any freeze exception, how long each takes, and whether they can run in parallel",
        source: "",
        category: "Remediation",
      },
      {
        text: "Amazon's approval of the repairs the roof inspection finds (its maintenance request, and any freeze exception)",
        source: "",
        category: "Remediation",
      },
    ],
    source: "EUSDR 3.1.4; Best Practices s2.4",
  },
  {
    id: "dr3",
    lane: "design",
    chapter: 3,
    owner: "amazon",
    title: "Design approval (DR3)",
    desc: "",
    reviews: true,
    deps: ["design"],
    endDeps: ["gridoffer"],
    days: null,
    subs: [
      "Amazon technical review",
      "Landlord: the detailed pack submitted, then the landlord's sign-off",
      "Insurance review",
    ],
    needs: [
      {
        text: "The Engineer of Record who reviews the design: the landlord's, or one Amazon names",
        source: "MSPPA s1.1 Engineer of Record",
        category: "Contacts and process",
      },
      {
        text: "How Amazon runs the design review: DR2 or not, the DR3 workflow and platform, who reviews, and how long it takes",
        source: "MSPPA s3.7(a); EUSDR Exhibit 9 s1.1",
        category: "Contacts and process",
      },
      {
        text: "The insurer requirements the insurance review checks the design against",
        source: "SPPA Sch 11 cl 3.7",
        category: "Insurance",
      },
    ],
    source: "MSPPA s3.3(b), s3.6, s3.7; EUSDR Exhibit 9 s1.1.12",
  },
  {
    id: "ntp",
    lane: "site",
    chapter: 4,
    owner: "amazon",
    title: "Notice to Proceed",
    desc: "Install start",
    deps: ["dr3", "pcg", "llsignoff"],
    days: 0,
    subs: [
      "No mobilisation date until Amazon and the landlord give NTP",
      "Contract install start: the Schedule 1 date, or 30 days after the design approval date if later",
    ],
    needs: [],
    source:
      'MSPPA "Installation Start Date"; Best Practices s1; EUSDR Exhibit 9 s1.6.7',
  },
  {
    id: "premob",
    lane: "site",
    chapter: 4,
    owner: "inrange",
    title: "Pre-mobilisation",
    desc: "Needs the roof ready",
    deps: ["ntp", "roof"],
    days: 30,
    subs: [
      "Kick-off with site operations, safety and facilities",
      "H&S, staging, roof protection and crane plans; insurance certificates",
      "Amazon approves the execution, quality and inspection plans",
    ],
    needs: [
      {
        text: "Documents Amazon holds that we reasonably need for pre-installation tests, before the installation start date",
        source: "MSPPA s2.5",
        category: "Documentation",
      },
      {
        text: "The date the site passes from the building contractor to Amazon, which sets the EHS rules we work under",
        source: "Best Practices s1",
        category: "Contacts and process",
      },
      {
        text: "Amazon's prior written consent to each installation or O&M subcontractor not on the SPPA's List of Subcontractors",
        source: "MSPPA s5.7(a)",
        category: "Contacts and process",
      },
      {
        text: "Right Crowd site access for every worker, requested at least two weeks ahead, and the compliance audits that gate site access",
        source: "Best Practices s1, s2.4; MSPPA s8.2(c)(iv)",
        category: "Contacts and process",
      },
      {
        text: "Amazon Solar Team acceptance of the pre-mobilisation submittals: H&S, staging and roof protection plans, quality plans and the schedule",
        source: "Best Practices s1",
        category: "Documentation",
      },
      {
        text: "Written pre-approval of each power cut and any yard occupation the works need, by Amazon and by the landlord where it applies",
        source: "MSPPA s4.2(c)",
        category: "Contacts and process",
      },
    ],
    source: "Best Practices s1-s4; EUSDR Exhibit 9 s1.6.2, s1.7.3",
  },
  {
    id: "install",
    lane: "site",
    chapter: 4,
    owner: "inrange",
    title: "Installation",
    desc: "~2 months per MWp",
    deps: ["premob"],
    days: null,
    subs: [
      "Mobilise and load the roof",
      "Mechanical attachments, each with a watertightness check our QC manager supervises (needs the roof inspection)",
      "Switchgear discovery (method statement 21 days ahead)",
      "Amazon quality inspections at 30-60% and at mechanical completion",
      "Installation carries on in winter, but no power interruptions 1 Oct to 15 Jan and cranes off site by the end of 14 Oct",
    ],
    needs: [],
    source:
      "SPPA Sch 1, Sch 9; MSPPA s5.8; EUSDR Exhibit 9 s1.6.6, s2.1(5), s2.7, s3.11",
  },
  {
    id: "roofcheck",
    lane: "site",
    chapter: 5,
    owner: "inrange",
    title: "Roof inspection after install",
    desc: "Leak test · Amazon and landlord may review",
    deps: ["install"],
    days: 14,
    subs: [
      "An independent roof inspector, not involved in the install, inspects the whole roof again with a water leakage test",
      "Amazon, the landlord and their consultants may review the roof to confirm the install did not damage it",
      "Any damage to the roof or the building repaired at our cost",
      "The roof damage and repair tracker goes into the as-builts and to Site Operations at the close-out meeting",
      "Not a COD condition: the COD checklist has no roof item",
    ],
    needs: [],
    source:
      "EUSDR 3.1.4(3), (5), (6), 3.1.6(8), 3.1.8(18), (19); SPPA Sch 11 s5",
  },
  {
    id: "commission",
    lane: "grid",
    chapter: 5,
    owner: "inrange",
    title: "Connect & commission",
    desc: "Needs the DNO connection",
    deps: ["install", "gridoffer"],
    days: 21,
    subs: [
      "Interconnection outage (method statement 60 days ahead)",
      "Amazon approves the third-party commissioner",
      "Commissioning plan 2 weeks before; Amazon has 10 Business Days, deemed accepted 20 later",
      "Commission to IEC 62446, 5-day performance test, DNO permission to operate",
    ],
    needs: [
      {
        text: "Amazon's review of the commissioning plan and test forms and of the independent commissioning firm, and its PAC test representative",
        source: "EUSDR Exhibit 9 s3.7(3), s4.2(b)",
      },
    ],
    source: "MSPPA s4.4; EUSDR Exhibit 9 s3.6, s3.7, s3.11, s4.2",
  },
  {
    id: "cod",
    lane: "site",
    chapter: 5,
    owner: "both",
    title: "COD",
    desc: "Amazon 10 days to acknowledge",
    deps: ["commission"],
    days: 0,
    subs: [
      "We give 5 Business Days notice, then confirm COD in writing with the COD checklist and lien waivers",
      "Amazon has 10 days to acknowledge, or COD is deemed approved",
      "Snag list closed within 60 days",
    ],
    needs: [
      {
        text: "Amazon's written acknowledgement of the COD checklist within 10 days of receiving it; with no reply in 10 days COD is deemed approved",
        source: "EUSDR Exhibit 9 s3.7(1)(h)",
      },
    ],
    source: "MSPPA s4.5; EUSDR Exhibit 9 s3.7.1; SPPA Sch 1",
  },
];

/** Design approval's parts: Amazon's technical review, the landlord's review (our pack,
 * then its sign-off) and Amazon's insurance review. Step 1 and the programme both name
 * them from here. */
export const DR3_PARTS: { owner: Owner; label: string }[] = [
  { owner: "amazon", label: "Amazon technical" },
  { owner: "inrange", label: "Detailed LL pack" },
  { owner: "third", label: "LL sign-off" },
  { owner: "amazon", label: "Amazon insurance" },
];

/** Keyed by the step the limit applies to. */
export const LIMITS: Record<string, StepLimit> = {
  pcg: {
    label: "Up to 30 days",
    risk: false,
    clause:
      "Amazon delivers a parent company guarantee from Amazon.com, Inc. within 30 days of the first SPPA.",
    reference:
      "SPPA Sch 2, s8.5(a) and (b)",
    text: "Within thirty (30) days following the Effective Date of the first SPPA entered into under this Main Agreement, the Purchaser shall procure and deliver to the Provider a parent company guarantee from Amazon.com, Inc. (the “Guarantor”) in the form set out in Schedule 16 to each SPPA (the “Parent Company Guarantee” or “PCG”), which Parent Company Guarantee shall, by its terms, guarantee the Purchaser’s payment obligations under this Main Agreement and each SPPA entered into under this Main Agreement from time to time, on a portfolio basis. For the purposes of this Section 8.5, “payment obligations” means any amounts payable by the Purchaser to the Provider under this Main Agreement or any SPPA.\n\nUntil the Parent Company Guarantee has been delivered to the Provider in accordance with Section 8.5(a) above, the Provider shall have no obligation to commence construction of, or deliver Delivered Output under, any SPPA, and any delay arising from such non-delivery shall entitle the Provider to a day-for-day extension of the Installation Start Date, Target Commercial Operation Date and any other milestone dates under the relevant SPPA in accordance with Section 4.5(c) of this Main Agreement.",
  },
  signoff: {
    label: "No time limit",
    risk: true,
    unbounded: true,
    clause:
      "Our structural study goes to Amazon for sign-off before full design starts. The requirements set no time limit for Amazon to sign it off, so detailed design can wait here indefinitely.",
    reference:
      "EUSDR 3.1.6, Structural considerations & building modifications",
    text: "[...] Study shall as well confirm and specify locations and maximum allowed capacity for materials to be loaded out. The study will be carried out in accordance with matching the Amazon structural Key Design Information KDI’s and will be issued for sign off by Amazon prior to full design start. The document containing the information about the study and results, is required to be signed by a Certified Engineer suitable for local regulations.",
  },
  dr3: {
    label: "Up to 104 business days",
    risk: true,
    clause:
      "Amazon has a Review Period of 90 Business Days from our Detailed Design Submission, and Purchaser Design Approval is due 14 Business Days after that: about 5 months. There is no deemed approval.",
    reference:
      'MSPPA definitions "Review Period" and "Purchaser Design Approval Date"',
    text: "“Review Period” means (i) in the case of the Landlord, the period from the Detailed Design Submission until the Landlord Approval Date, and (ii) in the case of the Purchaser, 90 Business Days from the Detailed Design Submission.\n\n“Purchaser Design Approval Date” means the date that is fourteen (14) Business Days from the last day of the Purchaser’s Review Period, as such date may be extended (a) if the Landlord Approval Date is extended in accordance with the definition of Landlord Approval Date, the Purchaser Design Approval Date shall be extended by the same amount of time, or (b) in accordance with the SPPA.",
  },
  cod: {
    label: "Amazon has 10 days",
    risk: false,
    clause:
      "We submit the COD checklist with its evidence. Amazon acknowledges in writing within 10 days, or COD is deemed approved.",
    reference:
      "EUSDR Exhibit 9 (PV End-to-End Deployment Process), s3.7 item 1(h)",
    text: "h) Commercial Operation Date (COD) Checklist – Submitted to Amazon. NOTE: Contractor must provide Amazon with checklist documenting evidence of COD as required. Amazon must acknowledge in writing, within 10 days of receiving the COD checklist that the conditions have been met. If Amazon does not respond within 10 days, COD is deemed approved.",
  },
};

const BY_ID = new Map(STEPS.map((s) => [s.id, s]));
export const stepById = (id: string): PathStep => {
  const step = BY_ID.get(id);
  if (!step) throw new Error(`unknown step ${id}`);
  return step;
};
